// ---------------------------------------------------------------------------
// seo.ts — what every public URL says about itself, in one place.
//
// ReCurr's marketing site is a ONE-PAGER (hero → features → how it works →
// pricing → contact → FAQ, all anchors on `/`) with four other public URLs
// hanging off it: /pricing, /legal, /legal/:docType and /status. That shape is
// what this file is built for, and it is why there is a table here rather than
// a per-page component: five kinds of page do not need a framework.
//
// THE PROBLEM IT SOLVES. ReCurr is a single-page app behind
// `try_files … /index.html`, so before this file every route served the same
// <title> and the same description. Fetched live on 2026-08-22, `/`, `/pricing`
// and `/legal/terms` returned BYTE-IDENTICAL head tags — three different pages
// competing as one search result.
//
// THREE RULES THIS FILE ENFORCES, each of which was got wrong somewhere before:
//
//   1. THE CANONICAL IS ALWAYS ON THE MARKETING HOST. /pricing, /legal* and
//      /status are served on BOTH hostnames (config/domains SHARED_PREFIXES),
//      so the same text is reachable at two URLs. app.recurr.co.za's own
//      robots.txt refuses crawlers, but a robots refusal is not a canonical:
//      a URL that is linked from elsewhere can still be indexed without being
//      crawled. Pointing every canonical at recurr.co.za is the actual defence.
//
//   2. PLATFORM PATHS GET NO CANONICAL AT ALL. A dashboard page is not a
//      duplicate of the home page, and saying it is would be worse than saying
//      nothing. `seoForPath` returns the site defaults with `canonicalPath:
//      null` for those, which removes the tag rather than pointing it at `/`.
//
//   3. NOTHING HERE INVENTS A `noindex`. It is tempting to mark every platform
//      route noindex from the client — and it would be wrong: `surfaceForPath`
//      classifies `/p/:merchantId/*` as 'platform', and those are MERCHANT
//      STOREFRONTS that must stay indexable on their own domains. Crawler
//      exclusion is the two robots.txt files' job (public/robots.txt and
//      public/robots-app.txt), which were verified working against production
//      on 2026-08-22. Do not add a meta robots tag here without re-reading
//      that classification.
//
// ── THE PRE-RENDERING DECISION, TAKEN RATHER THAN DEFERRED ──────────────────
//
// Search engines run JavaScript, so everything this file sets is read by the
// crawlers that decide ranking. SOCIAL LINK PREVIEWERS DO NOT — Facebook,
// LinkedIn, WhatsApp, Slack and X fetch the HTML once, read the tags in it, and
// never execute anything. So the question the chunk had to answer was whether
// the share card needed pre-rendering or server-side rendering to be right.
//
// THE ANSWER IS NO, AND THE REASON IS THE SHAPE OF THE SITE. The marketing site
// is a ONE-PAGER: `/` is the URL that gets pasted into a chat or a LinkedIn
// post, and index.html ships that page's complete card statically — title,
// description, canonical, og:*, twitter:* and a 1200×630 picture. The other
// four public URLs share it when pasted, which is imprecise but never wrong
// about the company, and each still gets its own exact tags for the crawlers
// that rank it.
//
// WHAT WOULD CHANGE THE ANSWER: a campaign landing page, or /pricing becoming
// something people paste on its own. Then the cost is a pre-render step in the
// build (render each SITEMAP_PATHS entry to its own HTML file), not a rewrite —
// the metadata is already a table, which is the part that would otherwise have
// to be invented.
//
// ▶ THAT TRIGGER HAS NOW BEEN PULLED, AND THE STEP IS NOT YET BUILT (C1 / M5
// part 1, 2026-10-10). /blog and its articles are the first pages on this site
// written to be found from a search and pasted on their own. They are served
// by the same SPA: Google and Bing render JavaScript, so they index; a social
// previewer shows the site card rather than the article's; and the AI search
// tools that fetch a page without running scripts see the empty shell. The
// pre-render step is the natural next chunk and is recorded as such in
// NEXT-CHUNKS.md — it is a build step, not a rewrite, exactly as this note
// said, and the per-article metadata it needs is already in config/blog.ts.
//
// Length discipline: descriptions are kept under ~160 characters because that
// is roughly where a search result truncates. `__tests__/seo.test.ts` fails the
// build if one grows past it — a truncated description is a sentence a visitor
// reads half of.
// ---------------------------------------------------------------------------

import { MARKETING_HOST, surfaceForPath } from './domains';
import { ALL_LEGAL_DOCS, legalDocBySlug } from './legalDocs';
import { BLOG_PATH, blogArticleBySlug, blogArticlePath, blogArticlePaths } from './blog';

/** Every canonical, every og:url and every sitemap entry is built on this. */
export const SITE_ORIGIN = `https://${MARKETING_HOST}`;

export const SITE_NAME = 'ReCurr';

/** en_ZA, not en_US: the product is ZAR-only and sold in one country. */
export const SITE_LOCALE = 'en_ZA';

/**
 * The share card. 1200×630 — the size Facebook, LinkedIn, WhatsApp, Slack and
 * X all crop to — generated by `tools/banners/draw_og_cover.py`.
 *
 * THE VERSION SUFFIX IN THE FILENAME IS LOAD-BEARING. This file lives in
 * public/, so it has no build hash, and nginx serves everything matching the
 * static-asset regex with `Cache-Control: public, immutable` for a year. Social
 * scrapers cache by URL even harder than browsers do — Facebook and LinkedIn
 * will hold a card for weeks. A new picture therefore needs a NEW FILENAME
 * (`-v3` next), not an overwrite; overwriting it changes nothing anybody can
 * see. `og-cover-v1.png` is deliberately still on disk for the same reason —
 * it is the card already in the field, and deleting it would break every
 * preview that has been scraped up to today.
 *
 * `-v2` was cut when the logo changed from "Powered by Cyberdummies" to
 * "Product of Cyberdummies" (M2), because the card is DRAWN FROM the logo file.
 * The name reaches seven places and missing one publishes a card nothing points
 * at — the generator, `index.html` (og:image, twitter:image and the
 * Organization JSON-LD's `logo`), `robots.txt`, this constant, and
 * `seo-manager.test.tsx`.
 */
export const OG_IMAGE_PATH = '/og-cover-v2.png';
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;
export const OG_IMAGE_ALT =
  'ReCurr — recurring billing that speaks South African. Subscriptions, invoices and failed-payment recovery in ZAR.';

export interface PageSeo {
  /** The <title>. Under ~60 characters or search results cut it. */
  title: string;
  /** meta[name=description]. Under ~160 characters — see the header. */
  description: string;
  /**
   * Path the canonical URL points at, always resolved against the MARKETING
   * host. `null` means this page gets no canonical and no og:url — which is
   * the honest answer for a platform page, not an oversight.
   */
  canonicalPath: string | null;
  /** og:title / og:description, when the share card should read differently. */
  ogTitle?: string;
  ogDescription?: string;
  /** og:type — `article` on a blog post. Absent means `website`. */
  ogType?: 'website' | 'article';
}

/**
 * The defaults — and they are not just a fallback. index.html ships EXACTLY
 * these values statically, because a social link previewer does not run
 * JavaScript and will only ever see the server's first response. Change one
 * here and change it there; `__tests__/seo.test.ts` checks the two agree.
 */
export const SITE_SEO: PageSeo = {
  title: 'ReCurr — Subscription billing for South Africa',
  description:
    'API-first subscription billing for South Africa. Recurring charges, invoices and pro-rata in ZAR through your own PayFast account, with failed-payment recovery.',
  canonicalPath: '/',
  ogTitle: 'ReCurr — Recurring billing that speaks South African',
  ogDescription:
    'Subscriptions, invoices and failed-payment recovery in ZAR — charged through your own PayFast account. API-first and POPIA-compliant.',
};

const PRICING_SEO: PageSeo = {
  title: 'Pricing — ReCurr subscription billing in ZAR',
  description:
    'One flat monthly fee in rands, never a percentage of what you collect. See what ReCurr costs for recurring billing, invoicing and failed-payment recovery.',
  canonicalPath: '/pricing',
};

const LEGAL_HUB_SEO: PageSeo = {
  title: 'Legal & policies — ReCurr',
  description:
    'ReCurr’s terms of service, privacy policy, DPA, SLA, acceptable use and PAIA manual — the agreements and disclosures that govern a ReCurr account.',
  canonicalPath: '/legal',
};

/**
 * M3's three pages. Each is a MARKETING path (config/domains MARKETING_PREFIXES)
 * rather than a shared one, so each gets its own canonical on recurr.co.za and
 * its own sitemap entry. `__tests__/seo.test.ts` fails on a duplicate title, a
 * duplicate description, a description over 160 characters, and on any sitemap
 * entry robots.txt would block — so the four remaining plumbing edits are all
 * checked by a test rather than by memory.
 */
const ABOUT_SEO: PageSeo = {
  title: 'About ReCurr — who builds it, and why',
  description:
    'ReCurr is built in Pretoria by Cyberdummies Online Solutions, a practice running since 2007. Why a South African billing platform exists, and what it is not.',
  canonicalPath: '/about',
};

const SECURITY_SEO: PageSeo = {
  title: 'Security & data protection — ReCurr',
  description:
    'How ReCurr protects your billing data: servers in South Africa, encrypted nightly off-site backups, no card numbers stored, and no settlement account of ours.',
  canonicalPath: '/security',
};

const DEVELOPERS_SEO: PageSeo = {
  title: 'Developers — the ReCurr API, SDKs and sandbox',
  description:
    'A documented, versioned REST API with webhooks, idempotent writes, Node and PHP libraries and a sandbox. Read the full integrator guide without an account.',
  canonicalPath: '/developers',
};

/**
 * A10 (2026-09-14) — the plugin-or-platform comparison. A marketing path like
 * M3's three, reached from the home page's banner above "Open books" and from
 * /about — not from the footer, by the founder's ruling.
 */
const PLUGIN_COMPARISON_SEO: PageSeo = {
  title: 'ReCurr plugin or platform — how the two compare',
  description:
    'The self-hosted ReCurr WooCommerce plugin beside the ReCurr platform: where each runs, what each costs, what each includes, and moving a store across.',
  canonicalPath: '/wordpress-plugin',
};

/**
 * B131 (2026-10-25) — the platform account, announced before it is sellable. A
 * marketing path like A10's comparison, reached from the block under the plan
 * cards on / and /pricing and from neither the header nor the footer. The
 * figures in the description are not here on purpose: they live once, in
 * planDisplay.PLATFORM_ACCOUNT, and a head tag is not read from it.
 */
const PLATFORM_ACCOUNT_SEO: PageSeo = {
  title: 'Platform account — ReCurr for you and the businesses under you',
  description:
    'One ReCurr account for your own billing and the businesses under you. Each is paid into its own PayFast account, or keeps its own software. One invoice a month.',
  canonicalPath: '/platform-account',
};

/**
 * C1 / M5 part 1 (2026-10-10) — the blog. The index has one entry here; each
 * article's entry is DERIVED from its register row in config/blog.ts by
 * `seoForPath`, so an article cannot be published without a head of its own.
 * An unknown slug gets the index's tags with no canonical, like an unknown
 * legal slug — the page it renders is the 404, which carries `noindex`.
 */
const BLOG_INDEX_SEO: PageSeo = {
  title: 'ReCurr blog — recurring billing in South Africa, explained',
  description:
    'Comparisons and guides on subscription billing for South African businesses: Stripe Billing, Chargebee, PayFast recurring payments and SARS invoices.',
  canonicalPath: BLOG_PATH,
};

const STATUS_SEO: PageSeo = {
  title: 'System status — ReCurr',
  description:
    'Live status for the ReCurr billing platform: the API, the dashboard, scheduled billing runs and payment processing, with any recent incidents.',
  canonicalPath: '/status',
};

/** Strip a trailing slash so /pricing and /pricing/ are one page, not two. */
function normalise(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname || '/';
}

/**
 * What this path should say about itself.
 *
 * Never returns null: a page that is not in the table gets the site defaults
 * WITHOUT a canonical, so navigating from /legal/tos into the dashboard cannot
 * leave the Terms' title and canonical behind on a page that is not the Terms.
 */
export function seoForPath(pathname: string): PageSeo {
  const path = normalise(pathname);

  if (path === '/') return SITE_SEO;
  if (path === '/pricing') return PRICING_SEO;
  if (path === '/status') return STATUS_SEO;
  if (path === '/about') return ABOUT_SEO;
  if (path === '/security') return SECURITY_SEO;
  if (path === '/developers') return DEVELOPERS_SEO;
  if (path === '/wordpress-plugin') return PLUGIN_COMPARISON_SEO;
  if (path === '/platform-account') return PLATFORM_ACCOUNT_SEO;
  if (path === BLOG_PATH) return BLOG_INDEX_SEO;
  if (path === '/legal') return LEGAL_HUB_SEO;

  if (path.startsWith(`${BLOG_PATH}/`)) {
    const article = blogArticleBySlug(path.slice(BLOG_PATH.length + 1));
    if (!article) return { ...BLOG_INDEX_SEO, canonicalPath: null };
    return {
      title: article.seoTitle,
      description: article.description,
      canonicalPath: blogArticlePath(article.slug),
      // The share card carries the article's own headline rather than the
      // search title: a headline is what a person pastes a link to.
      ogTitle: article.title,
      ogDescription: article.description,
      ogType: 'article',
    };
  }

  if (path.startsWith('/legal/')) {
    const doc = legalDocBySlug(path.slice('/legal/'.length));
    // An unknown slug is a 404 from the API, not a page — it gets the hub's
    // entry rather than a fabricated title for a document that does not exist.
    if (!doc) return { ...LEGAL_HUB_SEO, canonicalPath: null };
    return {
      title: `${doc.title} — ${SITE_NAME}`,
      description: doc.description,
      canonicalPath: `/legal/${doc.docType}`,
    };
  }

  return { ...SITE_SEO, canonicalPath: null };
}

/**
 * The head for a MERCHANT-SCOPED legal page — `/legal/:docType?merchant=<ref>`
 * (item B26).
 *
 * ⚠⚠ `SeoManager` DELIBERATELY STAYS SILENT ON `?merchant=`, AND SILENCE IS NOT
 * NEUTRAL. It declines to speak because the head this file writes for
 * `/legal/tos` names ReCurr — but declining leaves index.html's STATIC defaults
 * standing, and those are ReCurr's marketing defaults. Measured on production
 * 2026-09-21, `/legal/tos?merchant=rec10001` served:
 *
 *   <title>        ReCurr — Subscription billing for South Africa
 *   canonical      https://recurr.co.za/
 *
 * — so a merchant's contract announced itself as ReCurr's marketing page and
 * named ReCurr's HOME PAGE as its canonical URL. The page has to write its own
 * head; there is no third state where nobody does.
 *
 * ▶ NO CANONICAL, and that is the honest answer rather than an oversight. The
 * document is one merchant's, reached through a query string, on a host that is
 * not theirs — there is no URL that is the one true address for it. `applySeo`
 * drops og:url with it.
 *
 * ⚠ The company name is the server's, never the browser's — see
 * `EffectivePolicy.companyName` in services/policiesApi.
 */
export function merchantPolicySeo(docTitle: string, companyName: string): PageSeo {
  return {
    title: `${docTitle} — ${companyName}`,
    description: `${docTitle} for ${companyName}.`,
    canonicalPath: null,
  };
}

/**
 * The anonymous surfaces that wear a MERCHANT's brand, not ReCurr's.
 *
 * A storefront, a hosted checkout, a subscribe link and a settle-up link are
 * all served by this same bundle, and on all of them the visitor is a
 * merchant's customer who has never heard of ReCurr. BrandingContext repaints
 * them in the merchant's colours and PublicPageView titles them from the
 * merchant's own page metadata. Nothing in this file may overwrite that —
 * stamping "ReCurr — Subscription billing for South Africa" onto a browser tab
 * mid-checkout takes the customer off the brand they are paying, which is the
 * exact failure config/domains was written to prevent at the routing level.
 */
const MERCHANT_BRANDED_PREFIXES = ['/p/', '/store', '/subscribe/', '/pay/', '/checkout/'];

export function isMerchantBrandedPath(pathname: string): boolean {
  const path = normalise(pathname);
  return MERCHANT_BRANDED_PREFIXES.some((prefix) => path === prefix.replace(/\/$/, '') || path.startsWith(prefix));
}

/** True when this path is one the head manager should be speaking for at all. */
export function isPublicMarketingPath(pathname: string): boolean {
  const surface = surfaceForPath(normalise(pathname));
  return surface === 'marketing' || surface === 'shared';
}

/** An absolute URL on the marketing host, which is what every tag here needs. */
export function absoluteUrl(path: string): string {
  return `${SITE_ORIGIN}${path === '/' ? '/' : path}`;
}

// ---------------------------------------------------------------------------
// The sitemap
//
// One list, two consumers: `buildSitemapXml` writes public-facing XML at build
// time (see the recurrSitemap plugin in vite.config.ts) and the test suite
// asserts it holds every indexable route and nothing else. Before this, the
// site's own robots.txt advertised a sitemap that did not exist — and the
// failure was worse than a 404, because the SPA catch-all answered `200
// text/html` for it, so Search Console read a malformed sitemap rather than an
// absent one.
//
// ONLY MARKETING-HOST URLS BELONG HERE. Everything on app.recurr.co.za is
// login, signup, checkout and dashboard, and that host's own robots.txt
// refuses crawlers outright.
// ---------------------------------------------------------------------------

/** Every URL on this site that should be in a search index, in crawl order. */
export const SITEMAP_PATHS: string[] = [
  '/',
  '/pricing',
  '/about',
  '/security',
  '/developers',
  '/wordpress-plugin',
  '/platform-account',
  // The blog index, then every article newest first — one line per article is
  // NOT written here; the register in config/blog.ts is the list.
  BLOG_PATH,
  ...blogArticlePaths(),
  '/legal',
  ...ALL_LEGAL_DOCS.map((d) => `/legal/${d.docType}`),
  '/status',
];

/**
 * The sitemap, as XML.
 *
 * `<loc>` only, on purpose. `<priority>` and `<changefreq>` are documented by
 * Google as ignored. `<lastmod>` is not ignored — which is exactly why it is
 * absent: the only date available at build time is the build's own, every entry
 * would carry it, and a sitemap whose timestamps all move on every deploy is
 * one Google learns to distrust for the pages where the date would have
 * mattered. An accurate lastmod would have to come from the content, and the
 * legal documents' real dates live in the database.
 */
export function buildSitemapXml(origin: string = SITE_ORIGIN): string {
  const urls = SITEMAP_PATHS.map((p) => `  <url><loc>${origin}${p}</loc></url>`).join('\n');
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<!-- Generated from SITEMAP_PATHS in frontend/src/config/seo.ts. Do not hand-edit. -->',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    urls,
    '</urlset>',
    '',
  ].join('\n');
}
